import { analytics } from '@/config/firebase';
import { logEvent as firebaseLogEvent } from 'firebase/analytics';
import { useConsentStore } from '@/stores/consentStore'; 

interface FirebaseEventParams { [key: string]: any; }
export const logFirebaseEvent = (eventName: string, params?: FirebaseEventParams): void => {
  const { consent } = useConsentStore.getState(); 
  if (analytics && consent.analytics === 'granted') { 
    try {
      firebaseLogEvent(analytics, eventName, params);
    } catch (error) {
      console.error(`Error logging Firebase Analytics event: ${eventName}`, error);
    }
  }
};

declare global { 
  interface Window {
    fbq?: (...args: any[]) => void; 
    rdt?: { (action: string, eventNameOrPixelId: string, eventDataOrOptions?: object): void; sendEvent?: (...args: any[]) => void; callQueue?: any[]; };
    dataLayer?: any[]; gtag?: (...args: any[]) => void;
    ttq?: { (action: string, eventNameOrPixelId: string, eventDataOrOptions?: object): void; page: (...args: any[]) => void; track: (...args: any[]) => void; load: (pixelId: string) => void; instance: (pixelId: string) => any; _i?: any; methods?: string[]; setAndDefer?: (...args: any[]) => void; };
    $crisp?: any[];
    CRISP_WEBSITE_ID?: string;
  }
}

let isFbPixelInitialized = false;
let isRedditPixelInitialized = false;
let isGoogleTagInitialized = false;
let isTikTokPixelInitialized = false;
let isCrispChatInitialized = false;

const appendScriptOnce = (scriptId: string, src: string) => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(scriptId)) return;
  const s = document.createElement('script');
  s.id = scriptId;
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

const ensureFacebookPixelBase = () => {
  if (typeof window === 'undefined') return;
  if (!window.fbq) {
    const fbq = function (...args: any[]) {
      ((fbq as any).queue = (fbq as any).queue || []).push(args);
    } as any;
    (fbq as any).queue = [];
    (fbq as any).loaded = true;
    (fbq as any).version = '2.0';
    window.fbq = fbq;
    (window as any)._fbq = fbq;
  }
  appendScriptOnce('fb-pixel-base', 'https://connect.facebook.net/en_US/fbevents.js');
};

const ensureRedditPixelBase = () => {
  if (typeof window === 'undefined') return;
  if (!window.rdt) {
    const rdt = function (...args: any[]) {
      ((rdt as any).callQueue = (rdt as any).callQueue || []).push(args);
    } as any;
    (rdt as any).callQueue = [];
    window.rdt = rdt;
  }
  appendScriptOnce('reddit-pixel-base', 'https://www.redditstatic.com/ads/pixel.js');
};

const ensureTikTokPixelBase = () => {
  if (typeof window === 'undefined') return;
  if (!window.ttq) {
    const ttq: any = function (...args: any[]) {
      (ttq.q = ttq.q || []).push(args);
    };
    ttq.q = [];
    ttq.load = (id: string) => ttq('load', id);
    ttq.page = (...args: any[]) => ttq('page', ...args);
    ttq.track = (...args: any[]) => ttq('track', ...args);
    ttq.instance = () => ttq;
    window.ttq = ttq;
  }
  appendScriptOnce('tiktok-pixel-base', 'https://analytics.tiktok.com/i18n/pixel/events.js');
};

// --- Google Analytics Utilities ---
export const getGaClientId = (): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(/(?:^|; *)\_ga=([^;]*)/);
  if (match) {
    const gaCookie = decodeURIComponent(match[1]);
    // The client ID is the third part of the cookie string (e.g., GA1.1.XXXX.YYYY)
    const parts = gaCookie.split('.');
    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  }
  return undefined;
};

// --- Facebook Pixel (Advertising) ---
export const initFacebookPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isFbPixelInitialized || typeof window === 'undefined' || consent.advertising !== 'granted') {
    if (consent.advertising !== 'granted' && typeof window !== 'undefined' && window.fbq) console.log('Frontend: Facebook Pixel not initialized (consent advertising:', consent.advertising,')');
    return;
  }
  const pixelId = import.meta.env.VITE_FACEBOOK_PIXEL_ID;
  if (pixelId) {
    ensureFacebookPixelBase();
    try {
      if (typeof window.fbq !== 'function') return;
      window.fbq('init', pixelId);
      window.fbq('track', 'PageView');
      isFbPixelInitialized = true;
      console.log('Frontend: Facebook Pixel Initialized. ID:', pixelId);
    }
    catch (error) { console.error('Frontend: Error initializing Facebook Pixel:', error); }
  } else { console.log('Frontend: Facebook Pixel ID (VITE_FACEBOOK_PIXEL_ID) not found.'); }
};

export const trackFacebookEvent = (eventName: string, params?: object) => {
  const { consent } = useConsentStore.getState();
  if (!isFbPixelInitialized || typeof window === 'undefined' || typeof window.fbq !== 'function' || consent.advertising !== 'granted') {
    return;
  }
  try { window.fbq('track', eventName, params); }
  catch (error) { console.error(`Frontend: Error tracking FB Pixel event ${eventName}:`, error); }
};

// --- Reddit Pixel (Advertising) ---
export const initRedditPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isRedditPixelInitialized || typeof window === 'undefined' || consent.advertising !== 'granted') {
    if (consent.advertising !== 'granted' && typeof window !== 'undefined' && window.rdt) console.log('Frontend: Reddit Pixel not initialized (consent advertising:', consent.advertising,')');
    return;
  }
  const pixelId = import.meta.env.VITE_REDDIT_PIXEL_ID;
  if (pixelId) {
    ensureRedditPixelBase();
    try {
      if (typeof window.rdt !== 'function') return;
      window.rdt('init', pixelId, { "optOut": false, "useDecimalCurrencyValues": true });
      window.rdt('track', 'PageVisit');
      isRedditPixelInitialized = true;
      console.log('Frontend: Reddit Pixel Initialized.');
    }
    catch (e) { console.error('Frontend: Error init Reddit Pixel', e);}}
};
export const trackRedditEvent = (eventName: string, eventData?: object) => {
  const { consent } = useConsentStore.getState();
   if (!isRedditPixelInitialized || typeof window === 'undefined' || typeof window.rdt !== 'function' || consent.advertising !== 'granted') {
    return;
  }
  try { window.rdt('track', eventName, eventData); }
  catch (error) { console.error(`Frontend: Error tracking Reddit Pixel event ${eventName}:`, error); }
};

// --- Google Ads (gtag.js) ---
export const trackGoogleAdsConversion = (conversionLabel: string, params?: object) => {
  const { consent } = useConsentStore.getState();
  if (typeof window === 'undefined' || typeof window.gtag !== 'function' || consent.advertising !== 'granted') {
    return;
  }
  const conversionId = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID;
  if (!conversionId) { console.log('Frontend: Google Ads Conversion ID missing. Conversion not logged.'); return; }
  try { window.gtag('event', 'conversion', { 'send_to': `${conversionId}/${conversionLabel}`, ...params }); }
  catch (error) { console.error(`Frontend: Error tracking Google Ads conversion ${conversionLabel}:`, error); }
};


// --- TikTok Pixel (Advertising) ---
export const initTikTokPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isTikTokPixelInitialized || typeof window === 'undefined' || consent.advertising !== 'granted') {
    if (consent.advertising !== 'granted' && typeof window !== 'undefined' && window.ttq) console.log('Frontend: TikTok Pixel not initialized (consent advertising:', consent.advertising,')');
    return;
  }
  const pixelId = import.meta.env.VITE_TIKTOK_PIXEL_ID;
  if (pixelId) {
    ensureTikTokPixelBase();
    try {
      if (!window.ttq || typeof window.ttq.load !== 'function' || typeof window.ttq.page !== 'function') return;
      window.ttq.load(pixelId);
      window.ttq.page();
      isTikTokPixelInitialized = true;
      console.log('Frontend: TikTok Pixel Initialized. ID:', pixelId);
    }
    catch (error) { console.error('Frontend: Error initializing TikTok Pixel:', error); }
  } else { console.log('Frontend: TikTok Pixel ID (VITE_TIKTOK_PIXEL_ID) not found.'); }
};
export const trackTikTokEvent = (eventName: string, params?: object) => {
   const { consent } = useConsentStore.getState();
  if (!isTikTokPixelInitialized || typeof window === 'undefined' || !window.ttq || typeof window.ttq.track !== 'function' || consent.advertising !== 'granted') {
    return;
  }
  try { window.ttq.track(eventName, params); }
  catch (error) { console.error(`Frontend: Error tracking TikTok event ${eventName}:`, error); }
};

// --- Crisp Chat (Functional) ---
export const initCrispChat = () => {
  const { consent } = useConsentStore.getState();
  const crispWebsiteId = import.meta.env.VITE_CRISP_WEBSITE_ID;

  if (isCrispChatInitialized || typeof window === "undefined" || !crispWebsiteId) {
    if (!crispWebsiteId) console.log("Frontend: Crisp Chat ID (VITE_CRISP_WEBSITE_ID) not found.");
    return;
  }

  if (consent.functional === "granted") {
    try {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = crispWebsiteId;
      const d = document;
      const s = d.createElement("script");
      s.src = "https://client.crisp.chat/l.js";
      s.async = true;
      d.getElementsByTagName("head")[0].appendChild(s);
      isCrispChatInitialized = true;
      console.log("Frontend: Crisp Chat Initialized.");
    } catch (error) {
      console.error("Frontend: Error initializing Crisp Chat:", error);
    }
  } else {
    console.log("Frontend: Crisp Chat not initialized (consent functional:", consent.functional, ")");
  }
};

// ====================================================================================
// == UNIFIED BUSINESS EVENT TRACKING FUNCTIONS                                    ==
// ====================================================================================

/**
 * Tracks when a user copies their referral link, indicating an intent to share.
 * @param {object} data - The referral data.
 * @param {string} data.referralCode - The user's referral code.
 */
export const trackReferralLinkCopied = (data: { referralCode: string }) => {
  console.log(`[Tracking] Firing Referral Link Copied event. Code: ${data.referralCode}`);

  // GA4 - "share" is a recommended event name
  logFirebaseEvent('share', {
    method: 'copy_link',
    content_type: 'referral',
    item_id: data.referralCode,
  });

  // Meta (Facebook) - "Lead" can be used as a proxy for a high-value, non-purchase action
  trackFacebookEvent('Lead', {
    content_name: 'Referral Link Shared',
  });

  // Reddit - "Lead" is also appropriate here
  trackRedditEvent('Lead');

  // TikTok - "Share" is a standard event. We omit content_type as 'referral_program' is invalid.
  trackTikTokEvent('Share');
};

/**
 * Tracks the initiation of a checkout process.
 * @param {object} data - The checkout data.
 * @param {number} data.value - The value of the cart/plan.
 * @param {string} data.currency - The currency code (e.g., 'USD').
 * @param {string} data.planName - The name of the subscription plan.
 * @param {string} data.planId - The ID of the subscription plan.
 */
export const trackBeginCheckout = (data: { value: number; currency: string; planName: string; planId: string; }) => {
  console.log(`[Tracking] Firing Begin Checkout event for ${data.planName}. Value: ${data.value} ${data.currency}`);
  
  // GA4
  logFirebaseEvent('begin_checkout', {
    value: data.value,
    currency: data.currency,
    items: [{ item_id: data.planId, item_name: data.planName, quantity: 1, price: data.value }]
  });

  // Meta (Facebook)
  trackFacebookEvent('InitiateCheckout', {
    value: data.value,
    currency: data.currency,
    content_name: data.planName,
    content_ids: [data.planId],
  });

  // Reddit (Standard event 'Lead' is often used for this stage)
  trackRedditEvent('Lead');

  // TikTok
  trackTikTokEvent('InitiateCheckout', {
    value: data.value,
    currency: data.currency,
    content_id: data.planId,
    content_type: 'product',
    description: data.planName,
  });
};

/**
 * Tracks a successful purchase or subscription event (CLIENT-SIDE ONLY).
 * The authoritative event is tracked on the backend. This is for session attribution.
 * @param {object} data - The purchase data.
 * @param {number} data.value - The purchase value.
 * @param {string} data.currency - The currency code.
 * @param {string} data.transactionId - The unique transaction ID.
 * @param {string} data.planName - The name of the subscription plan.
 * @param {string} data.planId - The ID of the subscription plan.
 */
export const trackClientSidePurchase = (data: { value: number; currency: string; transactionId: string; planName: string; planId: string; }) => {
  console.log(`[Tracking] Firing CLIENT-SIDE Purchase event for session attribution.`);

  // 1. Google Analytics (GA4)
  logFirebaseEvent('purchase', {
    transaction_id: data.transactionId,
    value: data.value,
    currency: data.currency,
    items: [{ item_id: data.planId, item_name: data.planName, quantity: 1, price: data.value }]
  });

  // We DO NOT send events to other ad platforms here to avoid duplication with server-side events.
};

// ====================================================================================
// == INITIALIZATION LOGIC                                                         ==
// ====================================================================================

export const initializeAllTrackersBasedOnConsent = () => {
  console.log("Frontend: Attempting to initialize all trackers based on current consent...");
  initFacebookPixel();
  initRedditPixel();
  initTikTokPixel();
  initCrispChat();
};

useConsentStore.subscribe(
  (state, prevState) => {
    const functionalConsentChanged = state.consent.functional === 'granted' && prevState.consent.functional !== 'granted';
    const advertisingConsentChanged = state.consent.advertising === 'granted' && prevState.consent.advertising !== 'granted';
    const analyticsConsentChanged = state.consent.analytics === 'granted' && prevState.consent.analytics !== 'granted';

    if (state.hasMadeChoice && state.hasMadeChoice !== prevState.hasMadeChoice) {
        console.log("Frontend: Consent choice made/updated, re-evaluating tracker initialization.");
        initializeAllTrackersBasedOnConsent();
    } else if (functionalConsentChanged || advertisingConsentChanged || analyticsConsentChanged) {
        console.log("Frontend: Specific consent category granted, re-evaluating tracker initialization.");
        initializeAllTrackersBasedOnConsent();
    }
  }
);
